import React from "react";
import Router from "./routes/Router";
import { decrypt, decryptUrl } from "./utils/crypto";
import axiosInstance from "./services/api/interceptor/interceptor";
import { clearAuth, setUserProfileData, setUserAuth } from "./services/redux/slices/authSlice";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import LoaderBackdrop from "./components/ui/LoaderBackdrop";
import { resetStepperForAssistedLead } from "./services/redux/slices/stepperDynamicFormSlice";
import { setStatusPopup } from "./services/redux/slices/statusPopupSlice";
import { useSelector } from "react-redux";
import { RootState } from "./services/redux/store";
import { clearCacheOnceAndRequestReload } from "./utils/cacheCleaner";
import { setSelectedIndex, setSummaryPageActiveTab } from "./services/redux/slices/tabsSlice";

const resetPersistedTabs = () => {
  localStorage.removeItem("selectedIndex");
  localStorage.removeItem("persist:tabs");
  sessionStorage.removeItem("persist:tabs");
};

const App = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const [loader, setLoader] = React.useState(true);
  const processedAssistedDataRef = React.useRef<string>("");

  // Run once per session: clear Cache Storage + service workers, then reload to load latest bundle.
  useEffect(() => {
    let cancelled = false;
    clearCacheOnceAndRequestReload()
      .then((shouldReload) => {
        if (!cancelled && shouldReload) {
          window.location.reload();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("[App] Cache cleanup failed:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth?.authStatus);
  const { trackingId } = useSelector((state: RootState) => state.stepper);
  const phone_number = useSelector((state: RootState) =>
    state.auth?.userProfile?.phoneNumber ? decrypt(state.auth.userProfile.phoneNumber) : null
  );

  const resetTabsState = React.useCallback(() => {
    resetPersistedTabs();
    dispatch(setSelectedIndex(0));
    dispatch(setSummaryPageActiveTab("summary"));
  }, [dispatch]);

  const handleBypassLogin = async (value: string, assistedData: any) => {
    try {
      const resp: any = await axiosInstance.post(
        "api/v1/assisted/login",
        {
          trackingId: decrypt(value),
          key: assistedData?.key,
          key2: assistedData?.key,
        },
        {
          headers: {
            Authorization: 'Bearer'  + ' ' + assistedData?.bypassToken,
          },
        },
      ).then((resp: any) => {
        setLoader(false);
        const data: any = JSON.parse(decrypt(resp?.data));
        dispatch(setUserProfileData(data?.data?.user));
        dispatch(setUserAuth(data?.data?.token));

        // const urlParams: any = new URLSearchParams(location.search);
        // const rawData = urlParams.get("data");
        // if (rawData) {
        //   urlParams.delete("data");
        //   const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ""}`;
        //   window.history.replaceState({}, '', newUrl);
        // }

      });
    } catch (error: any) {
      dispatch(setStatusPopup({
        status: "error",
        message: "Something went wrong! Please try again later.",
      }));
      processedAssistedDataRef.current = "";
      setLoader(false);
    }
  }

  useEffect(() => {
    // Parse URL parameters properly using URLSearchParams
    const urlParams: any = new URLSearchParams(location.search);
    const rawData: any = urlParams.get("data");

    if (!rawData) {
      setLoader(false);
      return;
    }

    let data: any = null;
    try {
      data = JSON.parse(decryptUrl(rawData));
      console.log("Decrypted data:", data);
    } catch (err) {
      dispatch(setStatusPopup({
        status: "error",
        message: "The assisted journey data is invalid or could not be decrypted. Please try again.",
      }));
      setLoader(false);
      return;
    }

    const decodedTrackingId = decrypt(data?.trackingId);
    const decodedMobileNumber = decrypt(data?.mobileNumber);

    if (processedAssistedDataRef.current === rawData) {
      return;
    }

    if (decodedTrackingId != trackingId || decodedMobileNumber != phone_number) {
      if (data?.trackingId && data?.mobileNumber) {
        processedAssistedDataRef.current = rawData;
        setLoader(true);
        dispatch(clearAuth());
        dispatch(resetStepperForAssistedLead(decodedTrackingId));
        resetTabsState();
        handleBypassLogin(data?.trackingId, data);
      }
    } else {
      processedAssistedDataRef.current = rawData;
      setLoader(false);
    }
  }, [location, isAuthenticated, phone_number, trackingId, resetTabsState]);

  return (
    <div className="dashboard-contaner">
      {loader ? <LoaderBackdrop open={loader} /> :
        <>
          <Router />
        </>}

    </div>
  );
};

export default App;
